import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trophy, Calendar, ImageIcon, ChevronRight } from "lucide-react";
import { PageHero, PageBody, Lightbox, formatDate, slugify } from "./_shared";
import { useRoe } from "./RoeLayout";

export default function CampusEngagements() {
  const data = useRoe();
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const allEvents = data.events || [];
  // Filter by type slug if provided in URL
  const list = useMemo(() => {
    if (!typeSlug) return allEvents;
    return allEvents.filter((e) => slugify(e.event_type || "") === typeSlug.toLowerCase());
  }, [allEvents, typeSlug]);
  // Friendly label for the active type (use the first event's actual casing)
  const activeTypeLabel = typeSlug ? list[0]?.event_type || typeSlug : "";

  const [activeId, setActiveId] = useState(list[0]?.id || null);
  const [lbIdx, setLbIdx] = useState(-1);

  // When the typeSlug changes, reset selection to the first event in the new list
  useEffect(() => {
    setActiveId(list[0]?.id || null);
    setLbIdx(-1);
  }, [typeSlug, list]);

  const active = list.find((e) => e.id === activeId) || null;

  return (
    <div data-testid="roe-engagements">
      <PageHero
        eyebrow={typeSlug ? `Stories · ${activeTypeLabel}` : "Stories"}
        title="Campus Engagements"
        accent="coral"
      />
      <PageBody>
        {typeSlug && (
          <div className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-sm" data-testid="roe-engagements-filter-bar">
            <span className="text-slate-700">
              Showing <span className="font-semibold text-slate-900">{activeTypeLabel}</span> ({list.length})
            </span>
            <a
              href="../campus-engagements"
              onClick={(e) => { e.preventDefault(); navigate("../campus-engagements"); }}
              className="text-[11px] tracking-[0.15em] uppercase text-[#A57C00] hover:text-[#D4A017] font-semibold"
              data-testid="roe-engagements-clear-filter"
            >
              Show All
            </a>
          </div>
        )}
        {list.length === 0 ? (
          <div className="text-center bg-white rounded-xl shadow p-12 text-slate-500">
            {typeSlug
              ? `No ${activeTypeLabel} stories published yet.`
              : "Campus moments will appear here once the institute publishes them."}
          </div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6">
            {/* Left: list of engagements */}
            <aside
              className="bg-white rounded-xl shadow-md md:sticky md:top-24 self-start overflow-hidden"
              data-testid="roe-engagements-list"
            >
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#A57C00] font-semibold">
                  Browse
                </div>
                <div className="font-serif text-lg text-slate-900">
                  {typeSlug ? `${activeTypeLabel}` : "All Stories"} ({list.length})
                </div>
              </div>
              <ul className="max-h-[70vh] overflow-y-auto">
                {list.map((e) => {
                  const isActive = e.id === active?.id;
                  const isAch = (e.event_type || "").toLowerCase().includes("achievement");
                  return (
                    <li key={e.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveId(e.id);
                          setLbIdx(-1);
                        }}
                        data-testid={`roe-event-tab-${e.id}`}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 border-l-4 transition-colors ${
                          isActive
                            ? "bg-amber-50 border-amber-500"
                            : "border-transparent hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 text-white ${
                            isAch
                              ? "bg-gradient-to-br from-amber-400 to-yellow-500"
                              : "bg-gradient-to-br from-blue-600 to-indigo-600"
                          }`}
                        >
                          <Trophy size={14} strokeWidth={1.5} />
                        </span>
                        <span className="flex-1 min-w-0">
                          {e.event_type && (
                            <span
                              className={`inline-block text-[9px] tracking-[0.2em] uppercase font-bold px-1.5 py-0.5 rounded-sm mb-1 ${
                                isAch
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-blue-100 text-[#001F3F]"
                              }`}
                              data-testid={`roe-event-type-${e.id}`}
                            >
                              {e.event_type}
                            </span>
                          )}
                          <span
                            className={`block text-sm leading-snug ${
                              isActive
                                ? "text-slate-900 font-semibold"
                                : "text-slate-700"
                            }`}
                          >
                            {e.title}
                          </span>
                          <span className="block text-[11px] text-[#A57C00] mt-0.5">
                            {formatDate(e.event_date)}
                          </span>
                        </span>
                        <ChevronRight
                          size={14}
                          strokeWidth={1.5}
                          className={`mt-1 ${
                            isActive ? "text-[#A57C00]" : "text-slate-300"
                          }`}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: detail */}
            {active && (
              <article
                key={active.id}
                data-testid={`roe-event-${active.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-md"
              >
                {/* Meta strip */}
                <div className="px-6 sm:px-7 pt-6 flex flex-wrap items-center gap-3">
                  <span
                    className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full font-bold ${
                      (active.event_type || "").toLowerCase().includes("achievement")
                        ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-900"
                        : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                    }`}
                  >
                    {active.event_type}
                  </span>
                  <div className="flex items-center gap-1.5 text-xs text-[#A57C00] font-medium">
                    <Calendar size={12} strokeWidth={1.5} />
                    {formatDate(active.event_date)}
                  </div>
                </div>

                {/* Title + description */}
                <div className="px-6 sm:px-7 pt-3 pb-6">
                  <h2 className="font-serif text-2xl sm:text-3xl text-slate-900 leading-snug">
                    {active.title}
                  </h2>
                  {active.description && (
                    <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed whitespace-pre-line">
                      {active.description}
                    </p>
                  )}
                </div>

                {/* Photo strip */}
                {(active.photos || []).length > 0 && (
                  <div className="px-6 sm:px-7 pb-7">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                      <ImageIcon size={11} strokeWidth={1.5} />
                      Photos ({active.photos.length})
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory sm:flex-wrap sm:overflow-visible">
                      {active.photos.map((p, i) => (
                        <button
                          key={p.url + i}
                          type="button"
                          onClick={() => setLbIdx(i)}
                          className="shrink-0 w-40 h-28 sm:w-44 sm:h-32 rounded-lg overflow-hidden ring-1 ring-slate-200 hover:ring-[#D4A017] hover:ring-2 transition-all snap-start group"
                          aria-label={`Photo ${i + 1} of ${active.photos.length}`}
                        >
                          <img
                            src={p.url}
                            alt={`${active.title} — photo ${i + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            )}
          </div>
        )}
      </PageBody>
      {active && lbIdx >= 0 && (active.photos || []).length > 0 && (
        <Lightbox
          photos={(active.photos || []).map((p) => ({
            image_url: p.url,
            caption: active.title,
          }))}
          idx={lbIdx}
          onIdx={setLbIdx}
          onClose={() => setLbIdx(-1)}
        />
      )}
    </div>
  );
}
